import { useState, useEffect } from "react";
import Picker from "react-mobile-picker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

interface WheelTimePickerProps {
  value: string; // "HH:MM"
  onChange: (time: string) => void;
  placeholder?: string;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")); // 5-min steps

/**
 * iOS-style wheel time picker for mobile.
 * Shows a button that looks like an input; tapping opens a modal with wheel spinners.
 */
export default function WheelTimePicker({
  value,
  onChange,
  placeholder = "--:--",
  className,
}: WheelTimePickerProps) {
  const [open, setOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState(() => parseValue(value));

  // Sync external value changes
  useEffect(() => {
    setPickerValue(parseValue(value));
  }, [value]);

  const handleConfirm = () => {
    onChange(`${pickerValue.hour}:${pickerValue.minute}`);
    setOpen(false);
  };

  const handleCancel = () => {
    setPickerValue(parseValue(value));
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "h-12 px-3 rounded-md border border-input bg-background text-left w-full flex items-center gap-2 active:bg-muted transition-colors",
          className
        )}
      >
        <SrIcon name="clock" className="w-4 h-4 text-muted-foreground flex-shrink-0"  />
        <span className={cn("font-mono text-base", !value && "text-muted-foreground")}>
          {value || placeholder}
        </span>
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : handleCancel())}>
        <DialogContent className="max-w-sm p-0 gap-0">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-base">Velg klokkeslett</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Picker
              value={pickerValue}
              onChange={(v) => setPickerValue({ hour: v.hour, minute: v.minute })}
              wheelMode="normal"
              height={216}
              itemHeight={36}
            >
              <Picker.Column name="hour">
                {HOURS.map((h) => (
                  <Picker.Item key={h} value={h}>
                    {({ selected }) => (
                      <div className={cn(
                        "text-lg tabular-nums",
                        selected ? "text-foreground font-semibold" : "text-muted-foreground"
                      )}>
                        {h}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
              <Picker.Column name="minute">
                {MINUTES.map((m) => (
                  <Picker.Item key={m} value={m}>
                    {({ selected }) => (
                      <div className={cn(
                        "text-lg tabular-nums",
                        selected ? "text-foreground font-semibold" : "text-muted-foreground"
                      )}>
                        {m}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
            </Picker>
          </div>
          <DialogFooter className="px-4 py-3 border-t flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={handleCancel}>
              Avbryt
            </Button>
            <Button className="flex-1" onClick={handleConfirm}>
              Velg
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function parseValue(v: string): { hour: string; minute: string } {
  const match = /^(\d{2}):(\d{2})$/.exec(v);
  if (match) {
    // Snap minute to nearest 5-min step for picker display
    const m = parseInt(match[2]);
    const snapped = String(Math.round(m / 5) * 5).padStart(2, "0");
    return { hour: match[1], minute: snapped === "60" ? "00" : snapped };
  }
  const now = new Date();
  return {
    hour: String(now.getHours()).padStart(2, "0"),
    minute: String(Math.round(now.getMinutes() / 5) * 5).padStart(2, "0"),
  };
}
